import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './PDWebPlayerRoot.styled';
import VHPlayerHeader from '../VHPlayerHeader/VHPlayerHeader';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from '../../../store';
import { fetchCondition, resetDiagnosis } from '../../../slices/DiagnosisSlice';
import EndSessionPrompt from '../../EndSessionPrompt/EndSessionPrompt';
import {
  getActions,
  resetActions,
  getActionGroups,
} from '../../../slices/ActionSlice';
import {
  ChatStates,
  resetChat,
  selectChatState,
  selectTimeSpent,
  setIsPreviewPlayer,
  assessmentIdSelector,
  setScoreOverviewVisible,
  virtualHumanTalkingSelector,
} from '../../../slices/chat/ChatSlice';
import { setUser } from '../../../slices/UserSlice';
import {
  getPatient,
  patientStatusSelector,
  resetPatient,
} from '../../../slices/PatientSlice';
import ActionResultOverlay from '../../molecules/ActionResultOverlay/ActionResultOverlay';
import {
  onscreenObjectivesAndHintsEnabledSelector,
  resetUnitData,
  setCourseID,
  setNextUnit,
  setUnitID,
  setVirtualHumanData,
  virtualHumanDataSelector,
} from '../../../slices/VirtualHumanSlice';
import LoadingPageIndicator from '../../../../../../components/LoadingPageIndicator';
import { VirtualHumanProps } from '../../../proptypes/VirtualHuman';
import { usePrevious } from '../../../utility/StateHooks';
import {
  completeAnalyticsSessionStatusSelector,
  setSSEndpoint,
  startAnalyticsSession,
  startAnalyticsSessionStatusSelector,
} from '../../../slices/SilverstripeAPISlice';
import {
  useShouldShowHints,
  useVHTypeConfig,
} from '../../vh-types/vhTypeHooks';
import VHTypeOverlaySelector from '../../vh-types/vh-type-component-selectors/VHOverlaySelector/VHOverlaySelector';
import RootBehaviorHandlerSelector from '../../vh-types/vh-type-component-selectors/RootBehaviorHandlerSelector/RootBehaviorHandlerSelector';
import VhTypes from '../../vh-types/configs/VHTypes';
import {
  isEmbeddedPlayerSelector,
  isFullscreenSelector,
  setIsEmbedded,
  setMoreInfoVisible,
  setSidebarVisible,
} from '../../../slices/vh-player/VHPlayerSlice';
import ObjectiveHint from '../../ObjectiveHint/ObjectiveHint';
import WebPlayerTimer from '../../molecules/WebPlayerTimer/WebPlayerTimer';
import StatesTimer from '../../StatesTimer/StatesTimer';
import OverlaysVisibilityToolbar from '../../OverlaysVisibilityMenu/OverlaysVisibilityToolbar';
import ObjectivesLogicHandler from '../../ObjectivesLogicHandler/ObjectivesLogicHandler';
import useFeature from '../../../../../core/src/components/LaunchDarkly/hooks/useFeature';
import FullscreenVHButton from '../../FullscreenVHButton/FullscreenVHButton';
import VHWebModalContextProvider from '../../VHWebModalBase/VHWebModalBaseContext';
import VHWebModalBase from '../../VHWebModalBase/VHWebModalBase';
import { useRouterQuery } from '@virtidev/toolbox';
import { useDispatchAIGenerationSurveyStartEvent } from './useDispatchSurveyStartEvent';
import LegacyObjectiveGroupsList from '../../ObjectiveGroupsList/LegacyObjectiveGroupsList';
import FlashMessageHolder from '../../../../../../components/FlashMessageHolder';
import ScoreOverviewModalPopulator from '@vh/components/ScoreOverviewModalPopulator/ScoreOverviewModalPopulator';
import { useFlashMessage } from '@core/components/FlashMessage';
import tracker from '@core/helpers/tracker';
import {
  IsMobile,
  vhPlayerSupportsDevice,
  WeSupportUnity,
} from '@base/utility/BrowserCheck';
import { useVHScoreAvailableEmitter } from '@vh/components/organisms/PDWebPlayerRoot/useVHScoreAvailableEmitter';
import VHPlayerSelector from '@vh/components/VHPlayer/VHPlayerSelector';
import { useDispatchSessionCompleteIframeEvent } from '@vh/components/organisms/PDWebPlayerRoot/useDispatchSessionCompleteIframeEvent';
import VHOverlaySelector from '@vh/components/VHOverlays/VHOverlaySelector';
import RealtimeOpenAIProvider from '@vh/components/VHPlayer/RealtimeOpenAIPlayer/RealtimeOpenAI.context';
import MicrophoneStreamProvider from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import {
  getEvaluations,
  resetEvaluations,
} from '@vh/slices/EvaluationSlice';
import MobileGoLandscapeFullscreenPrompt from '@vh/components/MobileGoLandscapeFullscreenPrompt/MobileGoLandscapeFullscreenPrompt';
import { useFullscreenHandler } from '@vh/utility/useFullscreenHandler';

const PDWebPlayerApp = (props) => {
  const dispatch = useDispatch();
  const actions = useSelector((state) => state.action);
  const evaluationStore = useSelector((state) => state.evaluation);
  // const actionGroups = useSelector((state) => state.action);
  const virtualHumanData = useSelector(virtualHumanDataSelector);
  const chatState = useSelector(selectChatState);
  const prevChatState = usePrevious(chatState);
  const patient = useSelector((state) => state.patient.patient);
  const begunFetchingPatient = useSelector(
    (state) => state.patient.begunFetchingPatient
  );
  const patientStatus = useSelector(patientStatusSelector);
  const fetchingPatientStatus = useSelector((state) => state.patient.status);
  const vhTypeConfig = useVHTypeConfig();
  const finalScoreTimeSpent = useSelector(selectTimeSpent);
  const prevFinalScoreTimeSpent = usePrevious(finalScoreTimeSpent);
  const prevNode = useSelector((state) => state.chat.prevNode);
  const params = useRouterQuery();
  const isEmbedded = useSelector(isEmbeddedPlayerSelector);

  // set sidebar visible based on config
  useEffect(() => {
    dispatch(setSidebarVisible(vhTypeConfig.sidebarVisibleAtStart));
  }, [dispatch, vhTypeConfig.sidebarVisibleAtStart]);

  // ensure we set state for whether it's normal or embedded
  useEffect(() => {
    // only trigger once (was prev triggering on render due to params being reconstructed)
    if (isEmbedded !== props.isEmbedded) {
      dispatch(setIsEmbedded(props.isEmbedded ? true : false));
    }
  }, [params, dispatch, props.isEmbedded, isEmbedded]);

  // ensure we store preview player state on start
  useEffect(() => {
    dispatch(setIsPreviewPlayer(!!props.isPreviewPlayer));
  }, [props.isPreviewPlayer, dispatch]);

  // fetch patient if not already fetched
  useEffect(() => {
    if ((!patient || (patient && !patient.patient)) && !begunFetchingPatient) {
      dispatch(
        getPatient({
          ExternalID: props.virtualHumanData.ExternalID,
          ShareToken: props.virtualHumanData.ShareToken,
        })
      );
    }
  }, [patient, dispatch, props.virtualHumanData, begunFetchingPatient]);

  // set unitID if available
  useEffect(() => {
    if (props.unitID) {
      dispatch(setUnitID(props.unitID));
    }
  }, [dispatch, props.unitID]);

  // set courseID if available
  useEffect(() => {
    if (props.courseID) {
      dispatch(setCourseID(props.courseID));
    }
  }, [dispatch, props.courseID]);

  // set nextUnit if available
  useEffect(() => {
    if (props.nextUnit) {
      dispatch(setNextUnit(props.nextUnit));
    }
  }, [dispatch, props.nextUnit]);

  // fetch condition
  useEffect(() => {
    if (vhTypeConfig.usesCondition) {
      dispatch(fetchCondition(props.virtualHumanData.ExternalID));
    }
  }, [vhTypeConfig.usesCondition, dispatch, props.virtualHumanData.ExternalID]);

  // set user data passed into app into store
  useEffect(() => {
    dispatch(setUser(props.user));
  }, [dispatch, props.user]);

  // set virtual human data passed into app into store
  const EasyMode = props.virtualHumanData?.EasyMode ?? false;
  useEffect(() => {
    dispatch(setVirtualHumanData({ ...props.virtualHumanData, EasyMode }));
  }, [dispatch, props.virtualHumanData, EasyMode]);

  // set SS endpoint
  useEffect(() => {
    if (props.silverstripeEndPoint) {
      dispatch(setSSEndpoint(props.silverstripeEndPoint));
    }
  }, [props.silverstripeEndPoint, props.isPreviewPlayer, dispatch]);

  useDispatchAIGenerationSurveyStartEvent(
    props.isPreviewPlayer,
    props.virtualHumanData
  );
  // // generate deepgram api key
  // useEffect(() => {
  //   dispatch(getApiKey());
  //   return () => {
  //     dispatch(resetSpeechToText());
  //   };
  // }, [dispatch]);

  // once assessment id is updated, start the session
  const assessmentId = useSelector(assessmentIdSelector);
  const prevAssessmentId = usePrevious(assessmentId);
  useEffect(() => {
    if (
      !props.isPreviewPlayer &&
      props.silverstripeEndPoint &&
      assessmentId &&
      !prevAssessmentId
    ) {
      dispatch(startAnalyticsSession(props.silverstripeEndPoint));
    }
  }, [
    props.isPreviewPlayer,
    assessmentId,
    prevAssessmentId,
    props.silverstripeEndPoint,
    dispatch,
  ]);

  const startSessionStatus = useSelector(startAnalyticsSessionStatusSelector);
  const completeSessionStatus = useSelector(
    completeAnalyticsSessionStatusSelector
  );
  const { addFlashMessage } = useFlashMessage();
  useEffect(() => {
    if (startSessionStatus === 'error') {
      addFlashMessage(
        'The session was not started and will not be recorded, please reload the session.',
        'error'
      );
    }
  }, [startSessionStatus, addFlashMessage]);
  useEffect(() => {
    if (completeSessionStatus === 'error') {
      addFlashMessage('The session failed to be marked as complete.', 'error');
    }
  }, [completeSessionStatus, addFlashMessage]);

  useEffect(() => {
    if (!actions.actions && actions.status === 'idle') {
      dispatch(getActions(props.virtualHumanData.ExternalID));
    }
  }, [dispatch, actions, props.virtualHumanData]);

  useEffect(() => {
    if (!actions.groups && actions.status === 'idle') {
      dispatch(getActionGroups(props.virtualHumanData.ExternalID));
    }
  }, [dispatch, actions, props.virtualHumanData]);

  useEffect(() => {
    if (
      evaluationStore?.evaluations === null &&
      evaluationStore.status === 'idle'
    ) {
      dispatch(getEvaluations(props.virtualHumanData.ExternalID));
    }
  }, [dispatch, actions, props.virtualHumanData, evaluationStore]);

  useVHScoreAvailableEmitter();

  // ensure chat log and other data is reset when unmounting
  useEffect(() => {
    return () => {
      dispatch(resetChat());
      dispatch(setVirtualHumanData(null));
      dispatch(resetDiagnosis());
      dispatch(resetPatient());
      dispatch(resetActions());
      dispatch(resetUnitData());
      dispatch(resetEvaluations());
    };
  }, [dispatch]);

  // ensure end session dispatched when time spent score has been returned
  useEffect(() => {
    if (
      finalScoreTimeSpent &&
      finalScoreTimeSpent !== prevFinalScoreTimeSpent
    ) {
      vhTypeConfig.onFinalResultsSaved(dispatch);
    }
  }, [vhTypeConfig, prevFinalScoreTimeSpent, finalScoreTimeSpent, dispatch]);

  useEffect(() => {
    if (vhTypeConfig.type === VhTypes.BRANCHING) {
      if (prevNode && prevNode.action === 'end') {
        vhTypeConfig.onFinishSession(dispatch, patient);
      }
    }
  }, [prevNode, vhTypeConfig, dispatch, patient]);

  // show completed time message and score overview modal when both completed and vh is not/has finished talking
  const hasShownScoreOverview = useRef(false);
  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  const prevVirtualHumanTalking = usePrevious(virtualHumanTalking);
  useEffect(() => {
    if (
      chatState === ChatStates.COMPLETE &&
      !hasShownScoreOverview.current &&
      !virtualHumanTalking &&
      (prevChatState !== chatState ||
        prevVirtualHumanTalking !== virtualHumanTalking)
    ) {
      hasShownScoreOverview.current = true;
      dispatch(setScoreOverviewVisible(true));
    }
  }, [
    chatState,
    prevChatState,
    dispatch,
    virtualHumanTalking,
    prevVirtualHumanTalking,
  ]);

  const wrapperRef = useRef(null);
  const isSupportedDevice = vhPlayerSupportsDevice(
    props.virtualHumanData?.PlayerType
  );
  const isLoading =
    actions.status === 'pending' ||
    virtualHumanData === null ||
    fetchingPatientStatus === 'pending';
  const isError = actions.status === 'rejected' || patientStatus === 'rejected';
  const wrapperRefReady = isSupportedDevice && !isLoading && !isError;
  const isFullscreen = useSelector(isFullscreenSelector);
  useFullscreenHandler(wrapperRef, wrapperRefReady);

  const handleLoaded = useCallback(() => {
    if (!props.virtualHumanData) {
      return;
    }
    if (props.isEmbedded && props.sharetoken) {
      const org = props.virtualHumanData.Organisation || {};

      tracker.track('share_content_open', {
        type: 'virtualhuman',
        virtualhuman_id: `${props.virtualHumanData.ID}`,
        token: props.sharetoken,
        org_id: org.ID,
        org_name: org.Name,
        customer_stage: org.CustomerStage,
      });
      return;
    }

    tracker.track('vh_loaded', {
      virtualhuman_id: `${props.virtualHumanData.ID}`,
      user_id: props.user.ID,
    });
  }, [
    props.isEmbedded,
    props.sharetoken,
    props.user.ID,
    props.virtualHumanData,
  ]);

  useDispatchSessionCompleteIframeEvent();

  const handleMoreInfoClick = useCallback(() => {
    console.log('hey?');
    dispatch(setMoreInfoVisible(true));
  }, [dispatch]);

  useEffect(() => {
    if (
      props.isEmbedded &&
      chatState === ChatStates.COMPLETE &&
      props.virtualHumanData
    ) {
      const org = props.virtualHumanData.Organisation || {};

      tracker.track('share_content_completed', {
        type: 'virtualhuman',
        virtualhuman_id: `${props.virtualHumanData.ID}`,
        token: props.sharetoken,
        org_id: org.ID,
        org_name: org.Name,
        customer_stage: org.CustomerStage,
      });
    }
  }, [props.isEmbedded, chatState, props.sharetoken, props.virtualHumanData]);

  /**
   *
   * WARNING: if adding conditional rendering make sure to add relevant conditions to wrapperRefReady so that
   * the fullscreen event listener is applied correctly (otherwise it will attempt to apply the event listener
   * before it has been applied)
   *
   */
  if (!isSupportedDevice) {
    return (
      <Styled.Message>
        <div>
          Unfortunately, web preview is currently only available in latest
          version of Google Chrome, Mozilla Firefox, and Microsoft Edge on a
          computer.
        </div>
      </Styled.Message>
    );
  }

  if (isLoading) return <LoadingPageIndicator />;

  if (isError) return <div>Error retrieving data</div>;

  return (
    <VHWebModalContextProvider>
      <Styled.GlobalStyleForRootHeight />
      <Styled.Wrapper ref={wrapperRef}>
        {IsMobile() && (
          <MobileGoLandscapeFullscreenPrompt
            fullscreenElementRef={wrapperRef}
          />
        )}
        {/* <FullscreenHandler fullscreenElementRef={wrapperRef} /> */}
        <VHWebModalBase />
        <Styled.Content $isFullscreen={isFullscreen}>
          <RootBehaviorHandlerSelector />
          <EndSessionPrompt />
          <Styled.MainArea>
            <RealtimeOpenAIProvider
              vhId={props.virtualHumanData.ExternalID}
              shareToken={props.virtualHumanData.ShareToken}
            >
              <MicrophoneStreamProvider>
                <VHPlayerSelector
                  onLoaded={handleLoaded}
                  rootDirectory={props.rootDirectory}
                  vhId={props.virtualHumanData.ExternalID}
                  playerAreaRef={
                    wrapperRef && wrapperRef.current ? wrapperRef.current : null
                  }
                  avatarName={props.virtualHumanData.Avatar.Name}
                  environment={props.virtualHumanData?.Environment ?? null}
                  clothing={props.virtualHumanData?.Outfit?.Slug ?? null}
                  playerType={props.virtualHumanData.PlayerType}
                />
                <StatesTimer />
                {![ChatStates.LOADING].includes(chatState) && (
                  <>
                    <ObjectivesLogicHandler />
                    <ScoreOverviewModalPopulator />
                    <VHOverlaySelector wrapperRef={wrapperRef} />
                  </>
                )}
              </MicrophoneStreamProvider>
            </RealtimeOpenAIProvider>
          </Styled.MainArea>
        </Styled.Content>
      </Styled.Wrapper>
    </VHWebModalContextProvider>
  );
};

PDWebPlayerApp.propTypes = {
  // onSessionComplete: PropTypes.func.isRequired,
  rootDirectory: PropTypes.string.isRequired,
  user: PropTypes.shape({
    ID: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  }).isRequired,
  virtualHumanData: VirtualHumanProps.isRequired,
  silverstripeEndPoint: PropTypes.string.isRequired,
  unitID: PropTypes.number,
  isEmbedded: PropTypes.bool,
};

// separate wrapping component to set up the store and be able to reference it with hooks in the child
const PDWebPlayerRoot = (props) => {
  return (
    <Provider store={store}>
      <FlashMessageHolder />
      <PDWebPlayerApp
        rootDirectory={props.rootDirectory}
        user={props.user}
        virtualHumanData={props.virtualHumanData}
        silverstripeEndPoint={`${props.silverstripeEndPoint}graphql`}
        unitID={props.unitID}
        courseID={props.courseID}
        nextUnit={props.nextUnit}
        // onSessionComplete={props.onSessionComplete}
        isPreviewPlayer={props.isPreviewPlayer}
        isEmbedded={props.isEmbedded}
        sharetoken={props.sharetoken}
      />
    </Provider>
  );
};

PDWebPlayerRoot.defaultProps = {
  rootDirectory: '/',
  isPreviewPlayer: PropTypes.bool,
  isEmbedded: false,
};

PDWebPlayerRoot.propTypes = {
  // onSessionComplete: PropTypes.func.isRequired,
  rootDirectory: PropTypes.string.isRequired,
  user: PropTypes.shape({
    ID: PropTypes.string.isRequired,
    Name: PropTypes.string.isRequired,
  }).isRequired,
  virtualHumanData: VirtualHumanProps.isRequired,
  unitID: PropTypes.number,
  silverstripeEndPoint: PropTypes.string.isRequired,
  isEmbedded: PropTypes.bool,
};

export default PDWebPlayerRoot;
